import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getAudioUrl } from "@/lib/audio-utils";
import { useAnalytics } from "@/hooks/useAnalytics";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { motion } from "framer-motion";
import ChatVisualizationFlow from "@/components/ChatVisualizationFlow";
import VizOutlineReview, { type Outline } from "@/components/VizOutlineReview";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Heart,
  Volume2,
  Square,
} from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

interface Question {
  id: string;
  label: string;
  helper_text: string | null;
  input_type: string;
  sort_order: number;
  prompt_variable: string | null;
  options: Json;
  is_required: boolean;
  slider_min: number | null;
  slider_max: number | null;
  slider_labels: Json;
}

interface VoiceOption {
  id: string;
  name: string;
  description: string | null;
  preview_audio_url: string | null;
}

type Phase =
  | "questionnaire"
  | "voice"
  | "outline-loading"
  | "outline-review"
  | "generating"
  | "player"
  | "pre-rating"
  | "post-rating";

// SessionStorage helpers for persisting creation progress
const SESSION_KEY_PREFIX = "viz_create_";

function loadSessionState(vizType: string) {
  try {
    const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${vizType}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSessionState(vizType: string, state: Record<string, any>) {
  try {
    sessionStorage.setItem(`${SESSION_KEY_PREFIX}${vizType}`, JSON.stringify(state));
  } catch { /* quota exceeded – ignore */ }
}

function clearSessionState(vizType: string) {
  sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${vizType}`);
}

const CreateVisualization = () => {
  const { type } = useParams<{ type: string }>();
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  useEffect(() => {
    if (!subscription.loading && !subscription.subscribed) {
      toast.error("You need an active subscription to create visualizations.");
      navigate("/dashboard");
    }
  }, [subscription.loading, subscription.subscribed, navigate]);

  // Restore saved progress from sessionStorage
  const savedState = useRef(type ? loadSessionState(type) : null);

  const [phase, setPhase] = useState<Phase>(() => {
    const s = savedState.current;
    if (s?.phase && ["voice", "outline-review"].includes(s.phase)) return s.phase;
    return "questionnaire";
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [vizId, setVizId] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState("");
  const [vizData, setVizData] = useState<any>(null);
  const [signedAudioUrl, setSignedAudioUrl] = useState<string | null>(null);

  // Voice selection state
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(() => savedState.current?.selectedVoiceId ?? "");
  const [playingPreviewId, setPlayingPreviewId] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  // Pending data (stored after questionnaire, used when generating)
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, any>>(() => savedState.current?.pendingAnswers ?? {});
  const [pendingSportId, setPendingSportId] = useState<string | null>(() => savedState.current?.pendingSportId ?? null);

  // Approved outline (passed to generate-visualization)
  const [outline, setOutline] = useState<Outline | null>(() => savedState.current?.outline ?? null);

  // Player state
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [preRating, setPreRating] = useState(5);
  const [postRating, setPostRating] = useState(5);
  const [savingSession, setSavingSession] = useState(false);
  const [audioStartTime, setAudioStartTime] = useState<number | null>(null);

  // Persist restorable state to sessionStorage on changes
  useEffect(() => {
    if (!type) return;
    // Only persist phases where it makes sense to resume
    if (["questionnaire", "voice", "outline-review"].includes(phase)) {
      saveSessionState(type, {
        phase,
        pendingAnswers,
        pendingSportId,
        selectedVoiceId,
        outline,
      });
    }
  }, [type, phase, pendingAnswers, pendingSportId, selectedVoiceId, outline]);

  // Load questionnaire, voices, and profile in parallel
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      const [questionnaireResult, voicesResult, profileResult] = await Promise.all([
        supabase
          .from("questionnaires")
          .select("id")
          .eq("visualization_type", type as any)
          .eq("is_active", true)
          .limit(1)
          .single(),
        supabase
          .from("voice_options")
          .select("id, name, description, preview_audio_url")
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("profiles")
          .select("preferred_voice_id, sport_id")
          .eq("user_id", user.id)
          .single(),
      ]);

      if (!questionnaireResult.data) {
        toast.error("No questionnaire found for this type");
        navigate("/dashboard");
        return;
      }

      const { data: qs } = await supabase
        .from("questions")
        .select("*")
        .eq("questionnaire_id", questionnaireResult.data.id)
        .order("sort_order");

      if (qs) setQuestions(qs as Question[]);
      if (voicesResult.data) setVoices(voicesResult.data);
      if (profileResult.data?.preferred_voice_id) {
        setSelectedVoiceId(profileResult.data.preferred_voice_id);
      } else if (voicesResult.data?.length) {
        setSelectedVoiceId(voicesResult.data[0].id);
      }
      if (profileResult.data?.sport_id) {
        setPendingSportId(profileResult.data.sport_id);
      }

      setLoading(false);
    };
    loadData();
  }, [type, user, navigate]);

  // Voice preview helpers
  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPlayingPreviewId(null);
  }, []);

  const playPreview = useCallback(async (voiceId: string, previewUrl: string | null) => {
    if (playingPreviewId === voiceId) {
      stopPreview();
      return;
    }
    stopPreview();
    if (!previewUrl) {
      toast.error("No preview available for this voice");
      return;
    }
    setPlayingPreviewId(voiceId);
    const audio = new Audio(previewUrl);
    previewAudioRef.current = audio;
    audio.onended = () => setPlayingPreviewId(null);
    audio.onerror = () => {
      setPlayingPreviewId(null);
      toast.error("Voice preview failed to load");
    };
    try {
      await audio.play();
    } catch {
      setPlayingPreviewId(null);
      toast.error("Failed to play preview");
    }
  }, [playingPreviewId, stopPreview]);

  // Called when questionnaire completes — go to voice selection
  const handleQuestionnaireComplete = (answers: Record<string, any>) => {
    setPendingAnswers(answers);
    setPhase("voice");
  };

  // Called when voice is confirmed — generate outline
  const handleVoiceConfirm = async () => {
    stopPreview();
    await runGenerateOutline(pendingAnswers, pendingSportId);
  };

  const runGenerateOutline = async (
    answers: Record<string, any>,
    sportId: string | null,
    changeRequest?: string
  ) => {
    setPhase("outline-loading");

    const { data, error } = await supabase.functions.invoke("generate-outline", {
      body: {
        visualization_type: type,
        questionnaire_answers: answers,
        sport_id: sportId,
        ...(changeRequest ? { change_request: changeRequest } : {}),
      },
    });

    if (error || data?.error) {
      toast.error(error?.message || data?.error || "Failed to generate outline");
      setPhase("voice");
      return;
    }

    setOutline(data.outline as Outline);
    setPhase("outline-review");
  };

  // Called when athlete approves the outline
  const handleApproveOutline = async () => {
    if (!user || !outline) return;
    setPhase("generating");
    if (type) clearSessionState(type); // Progress is committed — clear saved draft
    setGenerationStatus("Creating your visualization...");

    try {
      const { data: viz, error: vizError } = await supabase
        .from("visualizations")
        .insert({
          user_id: user.id,
          visualization_type: type as any,
          questionnaire_answers: pendingAnswers as Json,
          sport_id: pendingSportId || null,
          voice_id: selectedVoiceId || null,
          generation_status: "pending",
        })
        .select()
        .single();

      if (vizError || !viz) throw vizError || new Error("Failed to create visualization");
      setVizId(viz.id);
      track("visualization_started", { visualization_type: type });

      setGenerationStatus("Writing your personalized script...");
      const { data: scriptData, error: scriptError } = await supabase.functions.invoke(
        "generate-visualization",
        { body: { visualization_id: viz.id, outline } }
      );
      if (scriptError) throw new Error(scriptError.message);
      if (scriptData?.error) {
        if (scriptData.error.includes("credit limit")) {
          toast.error(scriptData.error);
          navigate("/dashboard");
          return;
        }
        throw new Error(scriptData.error);
      }

      setGenerationStatus("Recording your session audio...");
      const { data: audioData, error: audioError } = await supabase.functions.invoke(
        "generate-audio",
        { body: { visualization_id: viz.id } }
      );
      if (audioError) throw new Error(audioError.message);
      if (audioData?.error) throw new Error(audioData.error);

      const { data: completed } = await supabase
        .from("visualizations")
        .select("*")
        .eq("id", viz.id)
        .single();

      if (completed) {
        const url = await getAudioUrl(completed.audio_storage_path, completed.audio_url);
        setSignedAudioUrl(url);
        setVizData(completed);
        setPhase("pre-rating");
      }
    } catch (err: any) {
      console.error("Generation error:", err);
      toast.error(err.message || "Failed to generate visualization");
      // Clean up the failed visualization record
      if (vizId) {
        await supabase.from("visualizations").delete().eq("id", vizId);
        setVizId(null);
      }
      setPhase("outline-review");
    }
  };

  // Player controls
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seek = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const skip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime + seconds);
    }
  };

  const toggleFavorite = async () => {
    if (!vizId) return;
    const newVal = !isFavorite;
    setIsFavorite(newVal);
    await supabase.from("visualizations").update({ is_favorite: newVal }).eq("id", vizId);
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // VOICE SELECTION PHASE
  if (phase === "voice") {
    return (
      <div className="min-h-screen gradient-hero flex flex-col">
        <header className="p-4 md:p-6 flex items-center gap-4 border-b border-border/30">
          <Button variant="ghost" size="icon" onClick={() => setPhase("questionnaire")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display text-lg font-semibold capitalize">{type} Session</h1>
            <p className="text-xs text-muted-foreground">Choose your voice</p>
          </div>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 py-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-lg"
          >
            <div className="mb-8 text-center">
              <h2 className="font-display text-2xl font-bold mb-2">Your Voice</h2>
              <p className="text-muted-foreground text-sm">
                Choose who guides your session. You can change your default anytime in Profile.
              </p>
            </div>

            {voices.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm">No voices available.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {voices.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVoiceId(v.id)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      selectedVoiceId === v.id
                        ? "border-primary bg-primary/10"
                        : "border-border/50 bg-secondary/30 hover:border-primary/30"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{v.name}</div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          playPreview(v.id, v.preview_audio_url);
                        }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          playingPreviewId === v.id
                            ? "bg-primary/20"
                            : "bg-secondary/60 hover:bg-primary/20"
                        }`}
                      >
                        {playingPreviewId === v.id ? (
                          <Square className="w-3.5 h-3.5 text-primary fill-primary" />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
                        )}
                      </button>
                    </div>
                    {v.description && (
                      <div className="text-xs text-muted-foreground mt-1">{v.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            <Button
              variant="hero"
              size="lg"
              className="w-full"
              onClick={handleVoiceConfirm}
              disabled={!selectedVoiceId && voices.length > 0}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // OUTLINE LOADING PHASE
  if (phase === "outline-loading") {
    return (
      <div className="min-h-screen gradient-hero flex flex-col items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-8 max-w-md"
        >
          <div className="w-24 h-24 rounded-full gradient-cta flex items-center justify-center mx-auto glow-primary">
            <Sparkles className="w-10 h-10 text-primary-foreground animate-pulse" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold mb-3">Building Your VIZ Outline</h2>
            <p className="text-muted-foreground">
              Planning your emotional arc, breath techniques, and sensory moments...
            </p>
          </div>
          <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
            <motion.div
              className="h-full gradient-cta"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 15, ease: "linear" }}
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // OUTLINE REVIEW PHASE
  if (phase === "outline-review" && outline) {
    return (
      <VizOutlineReview
        outline={outline}
        onApprove={handleApproveOutline}
        onChangeRequest={(request) =>
          runGenerateOutline(pendingAnswers, pendingSportId, request)
        }
        onBack={() => setPhase("voice")}
      />
    );
  }

  // GENERATING PHASE
  if (phase === "generating") {
    return (
      <div className="min-h-screen gradient-hero flex flex-col items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-8 max-w-md"
        >
          <div className="w-24 h-24 rounded-full gradient-cta flex items-center justify-center mx-auto glow-primary">
            <Sparkles className="w-10 h-10 text-primary-foreground animate-pulse" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold mb-3">Creating Your Session</h2>
            <p className="text-muted-foreground">{generationStatus}</p>
          </div>
          <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
            <motion.div
              className="h-full gradient-cta"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 60, ease: "linear" }}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            This usually takes 30–60 seconds
          </p>
        </motion.div>
      </div>
    );
  }

  // PRE-RATING PHASE
  if (phase === "pre-rating" && vizData) {
    return (
      <div className="min-h-screen gradient-hero flex flex-col items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md gradient-card rounded-2xl border border-border/50 shadow-card p-8"
        >
          <h2 className="font-display text-2xl font-bold text-center mb-2">Before You Listen</h2>
          <p className="text-sm text-muted-foreground text-center mb-8">
            {type === "recovery"
              ? "Rate how relaxed you feel right now so we can measure your progress."
              : "Rate your current mental readiness so we can measure your progress."}
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {type === "recovery" ? "Relaxation" : "Mental Readiness"}
              </span>
              <span className="font-display text-2xl font-bold text-primary">{preRating}</span>
            </div>
            <Slider min={1} max={10} step={1} value={[preRating]} onValueChange={([v]) => setPreRating(v)} />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{type === "recovery" ? "1 — Tense" : "1 — Not Ready"}</span>
              <span>{type === "recovery" ? "10 — Fully Relaxed" : "10 — Peak Ready"}</span>
            </div>
          </div>
          <Button
            variant="hero"
            size="lg"
            className="w-full"
            onClick={() => {
              setAudioStartTime(Date.now());
              setPhase("player");
              setTimeout(() => audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => {}), 300);
            }}
          >
            <Play className="w-4 h-4 mr-2" />
            Start Session
          </Button>
        </motion.div>
      </div>
    );
  }

  // POST-RATING PHASE
  if (phase === "post-rating" && vizData) {
    const lift = postRating - preRating;
    return (
      <div className="min-h-screen gradient-hero flex flex-col items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md gradient-card rounded-2xl border border-border/50 shadow-card p-8"
        >
          <h2 className="font-display text-2xl font-bold text-center mb-2">How Did That Feel?</h2>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {type === "recovery"
              ? "Rate how relaxed you feel now to track your progress."
              : "Rate your mental readiness now to track your lift."}
          </p>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50 mb-6">
            <span className="text-sm text-muted-foreground">Before session</span>
            <span className="font-semibold">{preRating} / 10</span>
          </div>
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">After session</span>
              <span className="font-display text-2xl font-bold text-primary">{postRating}</span>
            </div>
            <Slider min={1} max={10} step={1} value={[postRating]} onValueChange={([v]) => setPostRating(v)} />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 — Very Low</span>
              <span>10 — Peak</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/30 mb-8">
            <span className="text-sm font-medium">Performance Lift</span>
            <span className={`font-display text-lg font-bold ${lift > 0 ? "text-green-400" : lift < 0 ? "text-red-400" : "text-muted-foreground"}`}>
              {lift > 0 ? "+" : ""}{lift}
            </span>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                toast.success("Session complete!");
                navigate("/dashboard");
              }}
              disabled={savingSession}
            >
              Skip
            </Button>
            <Button
              variant="hero"
              className="flex-1"
              onClick={async () => {
                if (!user || !vizId) return;
                setSavingSession(true);
                try {
                  const durationSeconds = audioStartTime
                    ? Math.round((Date.now() - audioStartTime) / 1000)
                    : vizData.audio_duration_seconds || null;
                  await supabase.from("completed_sessions").insert({
                    user_id: user.id,
                    visualization_id: vizId,
                    duration_seconds: durationSeconds,
                    pre_mental_performance: preRating,
                    post_mental_performance: postRating,
                    mental_performance_lift: lift,
                    completed_at: new Date().toISOString(),
                  });
                  track("session_completed", { visualization_type: type, lift, duration_seconds: durationSeconds });
                  toast.success(lift > 0 ? `Great session! Mental performance +${lift} 🎯` : "Session recorded.");
                } catch {
                  toast.error("Session recorded but stats could not be saved.");
                } finally {
                  setSavingSession(false);
                  navigate("/dashboard");
                }
              }}
              disabled={savingSession}
            >
              {savingSession ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Session
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // PLAYER PHASE
  if (phase === "player" && vizData) {
    return (
      <div className="min-h-screen gradient-hero flex flex-col">
        <audio
          ref={audioRef}
          src={signedAudioUrl || undefined}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
          onEnded={() => {
            setIsPlaying(false);
            setPhase("post-rating");
          }}
          onError={async () => {
            // Re-fetch signed URL if it expired
            if (vizData?.audio_storage_path) {
              const newUrl = await getAudioUrl(vizData.audio_storage_path, vizData.audio_url);
              if (newUrl && newUrl !== signedAudioUrl) {
                setSignedAudioUrl(newUrl);
              }
            }
          }}
        />

        <header className="p-6 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm text-muted-foreground capitalize">{vizData.visualization_type} Session</span>
          <Button variant="ghost" size="icon" onClick={toggleFavorite}>
            <Heart className={`w-5 h-5 ${isFavorite ? "fill-primary text-primary" : ""}`} />
          </Button>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-lg mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-64 h-64 rounded-3xl gradient-card border border-border/50 shadow-card flex items-center justify-center mb-10"
          >
            <div className="text-center">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-3" />
              <span className="font-display text-lg font-semibold text-gradient-primary">VIZ</span>
            </div>
          </motion.div>

          <h1 className="font-display text-2xl font-bold text-center mb-1">{vizData.title}</h1>
          <p className="text-muted-foreground text-sm capitalize mb-8">{vizData.visualization_type} Visualization</p>

          <div className="w-full mb-4">
            <Slider
              value={[currentTime]}
              onValueChange={seek}
              max={duration || 1}
              step={0.1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex items-center gap-8">
            <button onClick={() => skip(-15)} className="text-muted-foreground hover:text-foreground transition-colors">
              <SkipBack className="w-6 h-6" />
            </button>
            <button
              onClick={togglePlay}
              className="w-16 h-16 rounded-full gradient-cta flex items-center justify-center glow-primary hover:scale-105 transition-transform"
            >
              {isPlaying ? (
                <Pause className="w-7 h-7 text-primary-foreground" />
              ) : (
                <Play className="w-7 h-7 text-primary-foreground ml-1" />
              )}
            </button>
            <button onClick={() => skip(15)} className="text-muted-foreground hover:text-foreground transition-colors">
              <SkipForward className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            Done
          </Button>
          <Button variant="hero" onClick={() => navigate("/library")}>
            View Library
          </Button>
        </div>
      </div>
    );
  }

  // QUESTIONNAIRE PHASE
  return (
    <ChatVisualizationFlow
      questions={questions}
      type={type || "competition"}
      onSubmit={handleQuestionnaireComplete}
      onBack={() => navigate("/dashboard")}
    />
  );
};

export default CreateVisualization;
